import React, { useMemo, useRef, useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  Card,
  Skeleton,
  useColorModeValue,
  Alert,
  AlertIcon,
  Text,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom'; // <-- Import navigate
import axios from 'axios';
import baseUrl from 'Base_Url/baseUrl';

const CastPieChart = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const labelColor = useColorModeValue('#090c10', '#E2E8F0');
  const chartRef = useRef(null);
  const navigate = useNavigate(); // <-- Use navigate

  const [casteData, setCasteData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCasteData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${baseUrl}/dashboard/survey-stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const casteCounts = response.data?.data?.casteCounts || {};
      const transformedData = Object.entries(casteCounts).map(
        ([key, value]) => ({
          name: key,
          value,
        }),
      );
      setCasteData(transformedData);
    } catch (error) {
      setError('Failed to load caste summary data');
      setCasteData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCasteData();
  }, []);

  const chartOptions = useMemo(
    () => ({
      title: {
        text: 'Overall Caste Summary',
        left: 'center',
        textStyle: {
          color: labelColor,
          fontSize: 18,
        },
      },
      tooltip: {
        trigger: 'item',
        // formatter: casteData.length > 0 ? '{b}: {c} ({d}%)' : '',
        formatter: casteData.length > 0 ? '{b}: {c}' : '',
      },
      legend: {
        bottom: 0,
        type: 'scroll',
        selectedMode: false,
        textStyle: {
          color: labelColor,
        },
      },
      series: [
        {
          name: 'Caste',
          type: 'pie',
          radius: ['40%', '65%'],
          label: {
            show: true,
            position: 'outside',
            formatter: '{b}',
            color: labelColor,
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold',
            },
          },
          labelLine: { show: true },
          data:
            casteData.length > 0 ? casteData : [{ name: 'No Data', value: 0 }],
        },
      ],
    }),
    [labelColor, casteData],
  );

  const onEvents = {
    click: (params) => {
      const caste = params?.name;
      if (caste && caste !== 'No Data') {
        navigate(
          `/admin/surveylist?category=${encodeURIComponent(
            JSON.stringify([caste]),
          )}`,
        );
      }
    },
  };

  return (
    <Card
      w="100%"
      mx="auto"
      p={1}
      bg={cardBg}
      shadow="md"
      borderRadius="md"
      mt={4}
    >
      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}
      {loading ? (
        <Skeleton height="300px" borderRadius="md" />
      ) : (
        <ReactECharts
          ref={chartRef}
          option={chartOptions}
          onEvents={onEvents}
          style={{ height: '300px', width: '100%' }}
          notMerge={true}
          lazyUpdate={true}
        />
      )}
    </Card>
  );
};

export default CastPieChart;
